import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSecretsApi, SecretMetadata } from '@/api/useSecretsApi';
import type { DataValue } from '@glueapp/graph-types';
import { isSecretReferenceValue } from '@glueapp/graph-types';
import { useQuery } from '@tanstack/react-query';

interface Props {
    portName: string;
    currentValue: DataValue | undefined;
    graphId: string;
    hasConnection: boolean;
    onValueChange: (value: DataValue | undefined) => void;
    onOpenSecretsPanel?: () => void;
}

export const SecretPortEditor: React.FC<Props> = ({
    portName,
    currentValue,
    graphId,
    hasConnection,
    onValueChange,
    onOpenSecretsPanel,
}) => {
    const api = useSecretsApi();

    const { data: availableSecrets = [], isLoading } = useQuery({
        queryKey: ['availableSecrets', graphId],
        queryFn: () => api.getAvailableSecrets(graphId),
    });

    const selectedName =
        isSecretReferenceValue(currentValue as DataValue) && (currentValue as any).secretName ? (currentValue as any).secretName : '';

    if (hasConnection) {
        return (
            <div className="px-3 py-2">
                <p className="text-xs text-neutral-500 dark:text-neutral-400">Value provided by connection</p>
            </div>
        );
    }

    // Group secrets by scope
    const graphSecrets = availableSecrets.filter(s => s.scope === 'GRAPH');
    const userSecrets = availableSecrets.filter(s => s.scope === 'USER');
    const orgSecrets = availableSecrets.filter(s => s.scope === 'ORG');

    return (
        <div className="px-3 py-2 flex flex-col gap-2 min-w-[200px]">
            <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">{portName}</label>
            <Select
                key={selectedName || '__empty__'}
                value={selectedName || undefined}
                onValueChange={name => {
                    onValueChange({ isSecretReferenceValue: true, secretName: name });
                }}
            >
                <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder={isLoading ? 'Loading...' : 'Select a secret...'} />
                </SelectTrigger>
                <SelectContent>
                    {graphSecrets.length > 0 && (
                        <>
                            <div className="px-2 py-1 text-[10px] font-semibold text-neutral-400 uppercase tracking-wider">Workflow</div>
                            {graphSecrets.map(s => (
                                <SelectItem key={s.id} value={s.name}>
                                    {s.name}
                                    {s.service && <span className="ml-1 text-neutral-400">({s.service})</span>}
                                </SelectItem>
                            ))}
                        </>
                    )}
                    {userSecrets.length > 0 && (
                        <>
                            <div className="px-2 py-1 text-[10px] font-semibold text-neutral-400 uppercase tracking-wider">Personal</div>
                            {userSecrets.map(s => (
                                <SelectItem key={s.id} value={s.name}>
                                    {s.name}
                                    {s.service && <span className="ml-1 text-neutral-400">({s.service})</span>}
                                </SelectItem>
                            ))}
                        </>
                    )}
                    {orgSecrets.length > 0 && (
                        <>
                            <div className="px-2 py-1 text-[10px] font-semibold text-neutral-400 uppercase tracking-wider">
                                Organization
                            </div>
                            {orgSecrets.map(s => (
                                <SelectItem key={s.id} value={s.name}>
                                    {s.name}
                                    {s.service && <span className="ml-1 text-neutral-400">({s.service})</span>}
                                </SelectItem>
                            ))}
                        </>
                    )}
                    {availableSecrets.length === 0 && !isLoading && (
                        <div className="px-2 py-2 text-xs text-neutral-400">No secrets available. Add one from the Secrets panel.</div>
                    )}
                </SelectContent>
            </Select>
            <div className="flex items-center gap-3">
                {selectedName && (
                    <button
                        onClick={() => onValueChange(undefined)}
                        className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 text-left"
                    >
                        Clear selection
                    </button>
                )}
                {onOpenSecretsPanel && (
                    <button
                        onClick={onOpenSecretsPanel}
                        className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 text-left"
                    >
                        Manage Secrets
                    </button>
                )}
            </div>
        </div>
    );
};
